import React, { useContext, useState } from "react";
import { Button, FormControl, Input, InputLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import GroupDCT from "../FieldTags/GroupDCT";
import { UserContext } from "../../context/UserContext";
import pullListLabels from "../../util/PullListLabels";
import ROLES from "../../util/roles";
import HistoryLogElement from "./HistoryLogElement";
import { checkRole } from "../../util/FormUtils";

const Tag = (props) => {
    const { label, onDelete, ...other } = props;

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        onDelete(label);
    };

    return (
        <div {...other}>
            <span>{label}</span>
            <CloseIcon onClick={handleDeleteClick} />
        </div>
    );
};

const StyledTag = styled(Tag)(
    ({ theme }) => `
        display: flex;
        align-items: center;
        height: 24px;
        margin: 2px;
        line-height: 22px;
        background-color: #f2f3f6;
        border: 1px solid gray;
        border-radius: 2em;
        box-sizing: content-box;
        padding: 0 4px 0 10px;
        outline: 0;
        overflow: hidden;
        cursor: pointer;
        transition: all 200ms ease;
  
        &:focus {
            border-color: ${
        theme.palette.mode === "dark" ? "#177ddc" : "#40a9ff"
    };
            background-color: ${
        theme.palette.mode === "dark" ? "#003b57" : "#e6f7ff"
    };
        }
  
        &:hover {
            background-color: #2155bc;
            border-color: #2155bc;
            color: #fff;
        }
  
        & span {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
  
        & svg {
            font-size: 12px;
            cursor: pointer;
            padding: 4px;
        }
    `
);

const RelatedJiraTickets = ({ relatedJiraTickets, handleChange, pullLists, informedConsent, informedConsentApprover, shipmentFromRepo, sampleDisposal }) => {
    const { user } = useContext(UserContext);
    const canEdit = ((shipmentFromRepo === "No" && sampleDisposal === "No") || (informedConsent !== "No" && informedConsentApprover !== "No")) && checkRole(user, [ROLES.DCT]);
    const [newTicket, setNewTicket] = useState("");

    const handleAddJiraTicket = () => {
        const isValidJiraLink = /^(https:\/\/jira\.benaroyaresearch\.org\/browse\/)?([a-zA-Z]+-\d+)$/.exec(newTicket);

        if (isValidJiraLink) {
            const [, , suffix] = isValidJiraLink;
            const currTickets = relatedJiraTickets.split(",");

            if (!currTickets.includes(suffix)) {
                const updatedTickets = [...currTickets, suffix];
                handleChange("relatedJiraTickets", updatedTickets.join(","));
            }
            setNewTicket("");
        }
        // Else warn that entered link is not valid
    };

    const handleRemoveJiraTicket = (ticketToRemove) => {
        const updatedTickets = relatedJiraTickets.split(",").filter((ticket) => ticket !== ticketToRemove);
        handleChange("relatedJiraTickets", updatedTickets.join(","));
    };

    const handleTagClick = (ticket) => {
        window.open(`https://jira.benaroyaresearch.org/browse/${ticket}`, "_blank");
    };

    return (
        <div className="planField">
            <GroupDCT />
            <div style={{ display: "flex", flexDirection: "column" }}>
                <FormControl style={{ display: "flex", alignItems: "center" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            gap: "2rem",
                        }}
                    >
                        <InputLabel shrink={true} style={{ marginLeft: "-13px" }}>
                            {pullListLabels.relatedJiraTickets}
                        </InputLabel>
                        <Input
                            id="relatedJiraTickets-field"
                            name="relatedJiraTickets"
                            notched={true}
                            value={newTicket}
                            onChange={(e) => setNewTicket(e.target.value)}
                            onBlur={handleAddJiraTicket}
                            style={{ flex: 1 }}
                            disabled={!canEdit}
                        />
                        <div className={canEdit ? "abled-button" : "disabled-button"}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddJiraTicket}
                                disabled={!canEdit}
                            >
                                + Add Jira Ticket
                            </Button>
                        </div>
                    </div>
                </FormControl>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        marginTop: "0.5rem",
                    }}
                >
                    {relatedJiraTickets.split(",").map((ticket) => {
                        if (ticket !== "") {
                            return (
                                <a
                                    href={`https://jira.benaroyaresearch.org/browse/${ticket}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <StyledTag
                                        label={ticket}
                                        onClick={() => handleTagClick(ticket)}
                                        onDelete={handleRemoveJiraTicket}
                                    />
                                </a>
                            );
                        }
                    })}
                </div>
            </div>
            <HistoryLogElement pullLists={pullLists} label={"relatedJiraTickets"} />
        </div>
    );
};

export default RelatedJiraTickets;